import { FC, Fragment, useContext, useEffect, useState } from 'react';
import DynamicTicketRoute from './TicketRoute';
import { DynamicFlight, HighlightRoute } from 'ts/dynamic/interfaces/DynamicResponse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlane } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import TicketRouteGds from './TicketRouteGds';
import Button from 'components/ui/Button/Button';
import { getCurrency } from 'utils/utils';
import { formatPriceDynamic } from 'utils/dynamic/dynamic';
import DynamicFlexDetails from './DynamicFlexDetails';
import { DynamicContext } from 'contexts/DynamicContext';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { isTT } from 'constants/generic';

interface DynamicTicketProps {
  dynamicFlight: DynamicFlight;
  onClickChange: (id: string) => void;
  onChangeFare?: (total: number, flight: DynamicFlight) => void;
  btnText: string;
  mode: number;
  choosenTicketPrice?: number;
  isLoading?: boolean;
  flexFlights?: DynamicFlight[];
  setUpdateFlight?: (arg: Date) => void;
  hideShadow?: boolean;
}

const DynamicTicket: FC<DynamicTicketProps> = ({ dynamicFlight: flight, ...props }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { currency } = useContext(DynamicContext);
  
  const [highlightedRoutes, setHighlightedRoutes] = useState<HighlightRoute[]>([]);
  const [highlightedGds, setHighlightedGds] = useState<HighlightRoute[]>([]);
  const higlightedGdsOutbound = highlightedGds.filter((gds) => gds.isOutbound);
  const higlightedGdsInbound = highlightedGds.filter((gds) => !gds.isOutbound);

  useEffect(() => {
    if (flight.inbound) {
      const arr: HighlightRoute[] = [flight.outbound, flight.inbound].map(() => ({
        departure: false,
        arrival: false,
      }));

      if (flight.outbound.arrivalAirport.code !== flight.inbound.departureAirport.code) {
        arr[0].arrival = true;
        arr[1].departure = true;
      }

      if (flight.outbound.gdsFlyAirs?.length && flight.inbound.gdsFlyAirs?.length) {
        const gdsArr = [...flight.outbound.gdsFlyAirs, ...flight.inbound.gdsFlyAirs].map(
          (fly, index) => ({
            departure: false,
            arrival: false,
            departureAirport: fly.departureAirport.code,
            arrivalAirport: fly.arrivalAirport.code,
            isOutbound: flight.outbound.gdsFlyAirs.length > index,
          })
        );

        for (let i = 0; i < gdsArr.length; i++) {
          if (!gdsArr[i + 1]) break;
          if (gdsArr[i].arrivalAirport !== gdsArr[i + 1].departureAirport) {
            gdsArr[i].arrival = true;
            gdsArr[i + 1].departure = true;
          }
        }

        setHighlightedGds(gdsArr);
      }
      setHighlightedRoutes(arr);
    } else if (flight.outbound) {
      const arr = [{ departure: false, arrival: false }];
      if (flight.outbound.gdsFlyAirs?.length) {
        const gdsArr = flight.outbound.gdsFlyAirs.map((fly) => ({
          departure: false,
          arrival: false,
          departureAirport: fly.departureAirport.code,
          arrivalAirport: fly.arrivalAirport.code,
        }));

        for (let i = 0; i < gdsArr.length; i++) {
          if (!gdsArr[i + 1]) break;
          if (gdsArr[i].arrivalAirport !== gdsArr[i + 1].departureAirport) {
            gdsArr[i].arrival = true;
            gdsArr[i + 1].departure = true;
          }
        }

        setHighlightedGds(gdsArr);
      }
      setHighlightedRoutes(arr);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={
        'flight-details-flex position-relative' +
        (props.mode === 1 ? ' ticket-choosen' : '') +
        (props.hideShadow ? '' : ' default-shadow flight-details-item')
      }
    >
      <div className="ticket-source ticket-source-dyn">{t(flight.flightSource)}</div>
      <div className={'ticket ticket-dynamic'}>
        <div className="ticket-routes">
          <DynamicTicketRoute
            bound={flight.outbound}
            firstRoute={true}
            highlighted={highlightedRoutes[0]}
            onClickInfo={() => setOpen(!open)}
          />
          <DynamicTicketRoute bound={flight.inbound} highlighted={highlightedRoutes[1]} />

          {open && (
            <div className="ticket-long">
              <div className="mb-20">
                {!!flight.outbound && (
                  <Fragment>
                    {!!flight.inbound && (
                      <span className="text-sm">
                        {' '}
                        <FontAwesomeIcon icon={faPlane} /> {t('flight.forward')}
                      </span>
                    )}

                    {flight.outbound.gdsFlyAirs?.map((_, index) => (
                      <TicketRouteGds
                        key={index}
                        outbond={flight.outbound}
                        gdsIndex={index}
                        highlighted={higlightedGdsOutbound[index]}
                      />
                    ))}
                  </Fragment>
                )}
              </div>
              <div>
                {!!flight.inbound && (
                  <span className="text-sm return">
                    <FontAwesomeIcon icon={faPlane} />
                    {t('flight.returning')}
                  </span>
                )}
                {flight.inbound?.gdsFlyAirs?.map((_, index) => (
                  <TicketRouteGds
                    key={index}
                    outbond={flight.inbound}
                    gdsIndex={index}
                    highlighted={higlightedGdsInbound[index]}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="ticket-price">
          <div className="flex-center flex-one display-column">
            {!!props.choosenTicketPrice && (
              <Fragment>
                <div className="text-light text-center mb-5">{t('dyn.extra_money')}</div>
                <div className="text-bold text-md mb-10">
                  {formatPriceDynamic(flight.priceDiff, getCurrency(currency as string))}
                </div>
              </Fragment>
            )}

            <Button
              title={props.btnText}
              onClick={() => props.onClickChange(flight.id)}
              disabled={props.isLoading}
            />

            {!isTT && !!props.choosenTicketPrice && flight.flightSource?.includes('ALP') && (
              <div className="mt-10 text-sm text-black">{flight.provider}</div>
            )}
          </div>
        </div>
      </div>

      {!!props.flexFlights && !!props.onChangeFare && (
        <DynamicFlexDetails
          flexFlights={props.flexFlights as DynamicFlight[]}
          onChooseFlex={props.onChangeFare}
          selectedFlight={flight}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(DynamicTicket, 'DynamicTicket');
